import { useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import type { NostrEvent } from '@nostrify/nostrify';

import { AppShell } from '@/components/AppShell';
import { PolaroidFeed } from '@/components/PolaroidFeed';
import { PhotoDetail } from '@/components/PhotoDetail';
import { usePictures } from '@/hooks/usePictures';

export default function GlobalFeed() {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = usePictures();
  const [selectedPhoto, setSelectedPhoto] = useState<NostrEvent | null>(null);

  // Flatten pages and deduplicate by event ID
  const pictures = useMemo(() => {
    const seen = new Set<string>();
    return (data?.pages.flat() ?? []).filter((event) => {
      if (seen.has(event.id)) return false;
      seen.add(event.id);
      return true;
    });
  }, [data?.pages]);

  const pageCount = data?.pages.length ?? 0;

  useSeoMeta({
    title: 'Global — Polaroids',
    description: 'All photos from the Nostr network.',
  });

  return (
    <AppShell>
      <PolaroidFeed
        pictures={pictures}
        isLoading={isLoading}
        hasNextPage={!!hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        pageCount={pageCount}
        onPhotoClick={setSelectedPhoto}
        showFooter
        emptyState={
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="bg-polaroid-white rounded-[3px] p-4 pb-12 border-2 border-dashed border-cream-dark shadow-sm w-48 mx-auto" style={{ transform: 'rotate(-2deg)' }}>
              <div className="aspect-[4/5] flex items-center justify-center bg-cream-dark/50 rounded-sm">
                <span className="text-4xl">📷</span>
              </div>
              <p className="font-hand text-pol-text-secondary text-center mt-2 text-lg">No photos yet...</p>
            </div>
            <p className="text-pol-text-secondary text-sm mt-4">Be the first to share a moment</p>
          </div>
        }
      />

      {/* Photo detail overlay */}
      {selectedPhoto && (
        <PhotoDetail
          event={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </AppShell>
  );
}
